import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Camera, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useGps } from "../lib/useGps";

const CATS = [
  ["cigar_lounge", "Lounge"], ["shop", "Cigar Shop"], ["rooftop", "Rooftop"],
  ["club", "Nightlife"], ["restaurant", "Restaurant"], ["hotel", "Hotel"],
  ["beach", "Beach"], ["bar", "Bar"], ["other", "Other"],
];

export default function Suggest() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0:here? 1:address 2:details
  const [here, setHere] = useState(null);
  const [coords, setCoords] = useState(null);
  const [form, setForm] = useState({ name: "", category: "cigar_lounge", address: "" });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const { requestGps } = useGps();

  const pickHere = async () => {
    // Route through LocationPrimer with the navigate context so the user sees
    // the branded "Get directions from where you are" card before the real
    // browser geolocation prompt fires.
    const g = await requestGps("navigate");
    if (g.ok) { setCoords({ lat: g.lat, lng: g.lng }); setHere(true); setStep(2); }
    else { toast.error("Location permission denied"); setHere(false); setStep(1); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.urls.medium);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/suggested-places", {
        name: form.name.trim(),
        category: form.category,
        address: form.address || null,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        photos: photoUrl ? [photoUrl] : [],
      });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (done) {
    return (
      <main className="min-h-screen grid place-items-center px-6 bg-background" data-testid="suggest-done">
        <div className="text-center">
          <span className="w-12 h-12 mx-auto grid place-items-center rounded-full bg-gold text-[hsl(var(--primary-foreground))]"><Check className="w-5 h-5" strokeWidth={2}/></span>
          <h1 className="font-display text-3xl mt-5 text-foreground">Thanks</h1>
          <p className="mt-2 text-sm text-foreground/60 max-w-xs mx-auto">We'll review your suggestion.</p>
          <button onClick={() => navigate("/")} className="mt-6 btn-outline py-2 px-4 text-xs">Back home</button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="suggest-page" className="min-h-screen pb-24 px-5 bg-background pt-6">
      <header className="flex items-center gap-3">
        <Link to="/" className="btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/></Link>
        <div>
          <div className="eyebrow">Suggest a place</div>
          <h1 className="font-display text-3xl mt-1 text-foreground">Add to Habano</h1>
        </div>
      </header>

      <div className="mt-8 lux-card p-5">
        {step === 0 && (
          <div data-testid="step-here">
            <h2 className="font-display text-2xl text-foreground">Are you here now?</h2>
            <p className="mt-2 text-sm text-foreground/55">If yes, we'll lock in your GPS as the location.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button data-testid="here-yes" onClick={pickHere} className="btn-gold py-3">Yes, I'm here</button>
              <button data-testid="here-no" onClick={() => { setHere(false); setStep(1); }} className="btn-outline py-3 text-xs">No, I'll add an address</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-address">
            <h2 className="font-display text-2xl text-foreground">Where is it?</h2>
            <label className="block mt-4">
              <div className="eyebrow mb-1">Address</div>
              <input data-testid="suggest-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="form-input" placeholder="e.g. Calle 23 #155, Vedado" />
            </label>
            <p className="mt-2 text-xs text-foreground/45">Pinning on a map will be available soon — your address is enough for now.</p>
            <button data-testid="address-next" onClick={() => setStep(2)} className="btn-gold py-2 px-4 text-xs mt-4">Continue</button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-details">
            {here && (
              <div className="text-[11px] text-gold mb-3 inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" strokeWidth={2}/> GPS locked</div>
            )}
            <label className="block">
              <div className="eyebrow mb-1">Name *</div>
              <input data-testid="suggest-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" />
            </label>
            <label className="block mt-3">
              <div className="eyebrow mb-1">Category *</div>
              <select data-testid="suggest-category" className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="block mt-3">
              <div className="eyebrow mb-1">Photo (optional)</div>
              <div className="flex items-center gap-3">
                {photoUrl && <img src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`} alt="" className="w-16 h-16 rounded-md object-cover border border-gold-soft" />}
                <label className="btn-outline py-2 px-3 text-xs cursor-pointer">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Camera className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                  {uploading ? "Uploading…" : (photoUrl ? "Replace" : "Add photo")}
                  <input data-testid="suggest-photo" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                </label>
              </div>
            </label>
            <button data-testid="suggest-submit" onClick={submit} className="btn-gold py-3 mt-5 w-full">Submit suggestion</button>
          </div>
        )}
      </div>
    </main>
  );
}
